/** WHO THE SERVED DATA WAS READ AS: the effective identity (the
 *  impersonated user's while impersonating) and the organisation. What the
 *  sync client keeps between opens (a draft's held segments) is kept per
 *  identity and dropped when it changes: impersonation starting or stopping,
 *  an organisation switch. Logout leaves the page, which drops everything.
 *  The auth hook sets it whenever `/auth/user` answers. */

let current: string | null = null;
let impersonating = false;
let byNodeId = false;
const listeners = new Set<(identity: string | null) => void>();

/** The organisation is `/auth/user`'s `organization_id`: the session's
 *  active one, which the server's checks use (VC-Authz, to come). Never
 *  `metadata.organization_id`: that is the sign-in provider's metadata,
 *  copied at sign-in and never updated (VC-Authz, 2026-10-12). With no
 *  organisation the key is the user alone (and nothing is kept in the
 *  browser's storage). */
export function servingIdentityOf(user: {
  identifier?: string; user_node_id?: string; impersonating?: boolean;
  impersonated_person?: { id?: string } | null;
  organization_id?: string | null;
} | null): string | null {
  if (!user) return null;
  // keyed by User node id: the impersonated person's while impersonating
  // (their sign-in identifier is never sent, VC-Authz 9db79b501), else my own
  const own = user.user_node_id ?? user.identifier;
  const who = user.impersonating ? user.impersonated_person?.id : own;
  if (!who) return null;
  return `${who}|${user.organization_id ?? ""}`;
}

export function getServingIdentity(): string | null {
  return current;
}

/** Whether the session impersonates (VC-Authz, 2026-10-19): nothing is
 *  kept in the browser's storage for an impersonated person. */
export function isServingImpersonating(): boolean {
  return impersonating;
}

/** Whether the identity's user is a User NODE id. /auth/user may leave
 *  `user_node_id` out (it is read in a try), and the identity then falls
 *  back to the sign-in identifier — an email: the island store keeps
 *  nothing then, never keyed by an email (VC-Authz, 2026-10-19). */
export function isServingIdentityByNodeId(): boolean {
  return byNodeId;
}

/** Whether `servingIdentityOf(user)` is keyed by a User node id. */
export function servingIdentityIsNodeId(user: {
  user_node_id?: string; impersonating?: boolean; impersonated_person?: { id?: string } | null;
} | null): boolean {
  if (!user) return false;
  return user.impersonating ? !!user.impersonated_person?.id : !!user.user_node_id;
}

export function setServingIdentity(identity: string | null, opts: { impersonating?: boolean; byNodeId?: boolean } = {}): void {
  const imp = !!opts.impersonating && identity !== null;
  const node = !!opts.byNodeId && identity !== null;
  if (identity === current && imp === impersonating && node === byNodeId) return;
  current = identity;
  impersonating = imp;
  byNodeId = node;
  for (const l of Array.from(listeners)) {
    try { l(identity); } catch { /* a listener never breaks the others */ }
  }
}

export function onServingIdentityChange(listener: (identity: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
